import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/base/SectionHeading";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";
import { filmFacts, siteStats } from "@/mocks/site";
import { viewingTips } from "@/mocks/guide";
import { usePageMeta } from "@/hooks/usePageMeta";

const heroImage =
  "https://readdy.ai/api/search-image?query=Stylized%20cinematic%20poster%20background%20inspired%20by%201940s%20Shanghai%20film%20noir%2C%20deep%20crimson%20velvet%20curtains%20and%20rain-soaked%20street%20reflections%2C%20silhouetted%20couple%20in%20elegant%20cheongsam%20and%20dark%20trench%20coat%2C%20dramatic%20chiaroscuro%20lighting%2C%20warm%20golden%20candle%20glow%2C%20heavy%20film%20grain%2C%20wide%20empty%20space%2C%20moody%20atmospheric%20editorial%20art%2C%20no%20text&width=1920&height=1080&seq=sejie-hero-cinema-01&orientation=landscape";

const posterImage =
  "https://readdy.ai/api/search-image?query=Vertical%20vintage%20movie%20poster%20style%20artwork%20inspired%20by%201940s%20Shanghai%2C%20elegant%20woman%20in%20a%20dark%20cheongsam%20standing%20in%20a%20shadowed%20doorway%2C%20ornate%20art%20deco%20interior%2C%20crimson%20and%20gold%20palette%2C%20smoky%20haze%2C%20dramatic%20side%20light%2C%20painterly%20retro%20illustration%2C%20fine%20film%20grain%20texture%2C%20no%20text%2C%20no%20lettering&width=900&height=1200&seq=sejie-poster-portrait-01&orientation=portrait";

const guideImage =
  "https://readdy.ai/api/search-image?query=Empty%20vintage%20cinema%20auditorium%20with%20rows%20of%20deep%20crimson%20velvet%20seats%2C%20single%20projector%20beam%20cutting%20through%20dusty%20air%2C%20warm%20amber%20wall%20sconces%2C%20ornate%20art%20deco%20ceiling%2C%20quiet%20atmospheric%20mood%2C%20cinematic%20interior%20photography%2C%20rich%20shadows%2C%20no%20text&width=1200&height=800&seq=sejie-guide-cinema-04&orientation=landscape";

export default function Home() {
  usePageMeta(
    "色戒未删减版电影资料大全与剧情解析",
    "色戒未删减版网站提供完整版电影介绍、剧情解析及相关影视资讯，帮助影迷深入了解《色戒》故事和拍摄背景，涵盖电影评论和观影指南。",
    "色戒未删减版,色戒完整版,色戒电影,剧情解析,电影介绍,影视资源,电影评论,观影指南",
  );

  const latest = [...articles]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 6);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative flex min-h-[640px] w-full items-center overflow-hidden pt-[72px] md:min-h-[700px]">
        <img
          src={heroImage}
          alt="《色戒》完整版专题视觉海报"
          title="色戒未删减版 - 完整版电影资料专题"
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/60" />

        <div className="relative z-10 mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8 md:py-20">
          <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="reveal flex flex-wrap items-center gap-3">
                <span className="rounded-full border border-accent-500/40 bg-background-950/60 px-3 py-1 font-label text-[10px] uppercase tracking-[0.24em] text-accent-300">
                  完整版专题
                </span>
                <span className="font-label text-[11px] tracking-[0.18em] text-foreground-400">
                  2007 · 李安 · 158 分钟
                </span>
              </div>
              <h1 className="reveal reveal-delay-1 mt-6 font-heading text-[34px] leading-[1.22] text-foreground-50 sm:text-[44px] md:text-[54px] md:leading-[1.15]">
                一部电影
                <br />
                值得被<span className="text-accent-400">完整</span>地讨论
              </h1>
              <p className="reveal reveal-delay-2 mt-6 max-w-xl text-sm leading-[1.9] text-foreground-300 md:text-[15px]">
                从版本差异、叙事结构到镜头与声音，我们把散落在各处的资料整理成一条清晰的观影路径。这里不提供播放，只提供理解一部电影所需要的耐心与细节。
              </p>
              <div className="reveal reveal-delay-3 mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/detail/complete-version-length"
                  className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm text-foreground-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-book-read-line text-base" />
                  阅读完整版介绍
                </Link>
                <Link
                  to="/special/plot-analysis"
                  className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-50/25 bg-background-950/40 px-6 text-sm text-foreground-100 backdrop-blur-sm transition-colors hover:border-accent-500/60 hover:text-accent-300"
                >
                  进入剧情解析专题
                  <i className="ri-arrow-right-line text-base" />
                </Link>
              </div>
            </div>

            <div className="reveal reveal-delay-3 w-full max-w-[300px] shrink-0 self-start rounded-lg border border-background-700/70 bg-background-950/70 p-3 backdrop-blur-md lg:self-center">
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-md">
                <img
                  src={posterImage}
                  alt="《色戒》完整版专题海报"
                  title="色戒完整版 - 电影海报"
                  className="h-full w-full object-cover object-top"
                />
                <span className="absolute left-3 top-3 rounded-sm bg-background-950/85 px-2 py-1 font-label text-[9px] uppercase tracking-[0.2em] text-accent-300">
                  Special Feature
                </span>
              </div>
              <div className="px-1 pb-1 pt-4">
                <h2 className="font-heading text-base text-foreground-50">
                  《色戒》完整版资料专题
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                  版本考据 · 剧情逐段解析 · 幕后与影评，共 11 篇深度内容。
                </p>
                <Link
                  to="/category"
                  className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-xs text-accent-300 transition-colors hover:text-accent-200"
                >
                  浏览全部资料
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-4 border-t border-foreground-50/10 pt-6 md:mt-16 md:grid-cols-4 md:gap-6">
            {siteStats.map((stat) => (
              <div key={stat.label} className="flex items-baseline gap-2">
                <span className="font-heading text-2xl text-accent-300 md:text-3xl">
                  {stat.value}
                </span>
                <span className="text-[11px] text-foreground-500">{stat.unit}</span>
                <span className="ml-1 hidden text-[11px] text-foreground-500 sm:inline">
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 电影介绍 */}
      <section className="border-t border-background-800/70 bg-background-950 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr] lg:gap-14">
            <div className="relative">
              <div className="overflow-hidden rounded-lg border border-background-800">
                <img
                  src={posterImage}
                  alt="《色戒》完整版电影海报竖版"
                  title="色戒未删减版 - 完整版海报"
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
              <div className="mt-3 flex items-center justify-between rounded-md border border-background-800/80 bg-background-900/60 px-4 py-3">
                <span className="text-[11px] text-foreground-500">资料状态</span>
                <span className="flex items-center gap-1.5 text-[11px] text-accent-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                  持续整理中
                </span>
              </div>
            </div>

            <div>
              <SectionHeading
                eyebrow="Film Profile"
                title="《色戒》完整版：一部关于伪装与失控的电影"
                desc="故事发生在上世纪四十年代的上海与香港。一群年轻人的理想、一次漫长的潜伏、以及两个人在试探中逐渐失控的关系，共同构成了这部电影的全部重量。"
              />
              <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                {filmFacts.map((fact) => (
                  <div
                    key={fact.label}
                    className="flex items-start justify-between gap-4 border-b border-background-800/70 pb-3"
                  >
                    <span className="shrink-0 text-xs text-foreground-500">
                      {fact.label}
                    </span>
                    <span className="text-right text-[13px] text-foreground-100">
                      {fact.value}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-8 space-y-4 text-sm leading-[1.95] text-foreground-400">
                <p>
                  完整版本最值得讨论的地方，不在于它多了几分钟，而在于它愿意让镜头停留。人物的犹豫、房间里的空气、一句没说完的话，都被保留在画面里，构成一种缓慢累积的压迫感。
                </p>
                <p>
                  我们的资料从版本口径开始，逐步进入叙事结构、场景调度、服装造型与声音设计。你可以按顺序阅读，也可以从自己最在意的细节切入——专题页提供了完整的目录索引。
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/detail/novel-to-screen"
                  className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 text-sm text-foreground-200 transition-colors hover:border-accent-600/60 hover:text-accent-300"
                >
                  从小说到银幕
                  <i className="ri-arrow-right-up-line" />
                </Link>
                <Link
                  to="/guide"
                  className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 text-sm text-foreground-200 transition-colors hover:border-accent-600/60 hover:text-accent-300"
                >
                  观影指南
                  <i className="ri-compass-3-line" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 最新影评与解析 */}
      <section className="border-t border-background-800/70 bg-background-900/30 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8">
          <SectionHeading
            eyebrow="Latest Writing"
            title="最新影评与剧情解析"
            desc="按发布时间整理的深度内容，从表演、镜头到声音设计，逐层拆解这部电影。"
            action={{ label: "查看全部内容", to: "/category" }}
          />
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {latest.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* 观影指南推荐 */}
      <section className="border-t border-background-800/70 bg-primary-950/60 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-14">
            <div>
              <SectionHeading
                eyebrow="Viewing Guide"
                title="观影技巧与指南推荐"
                desc="完整版需要合适的观看方式。我们整理了时间安排、注意力分配与讨论节奏的建议，也收录了幕后与资料线索。"
              />
              <ul className="mt-8 space-y-5">
                {viewingTips.slice(0, 3).map((tip, index) => (
                  <li key={tip.title} className="flex gap-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-accent-500/40 bg-background-950/50 text-accent-300">
                      <i className={`${tip.icon} text-base`} />
                    </span>
                    <div>
                      <h3 className="font-heading text-[15px] text-foreground-50">
                        {tip.title}
                      </h3>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-primary-100/70">
                        {tip.body}
                      </p>
                    </div>
                    <span className="ml-auto hidden font-label text-xs text-primary-200/40 sm:block">
                      0{index + 1}
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/guide"
                className="mt-8 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm text-background-950 transition-colors hover:bg-accent-400"
              >
                打开观影指南
                <i className="ri-arrow-right-line text-base" />
              </Link>
            </div>
            <div className="relative overflow-hidden rounded-lg border border-primary-900">
              <img
                src={guideImage}
                alt="影院放映厅与完整版观影环境示意"
                title="色戒完整版 - 观影环境与指南"
                className="aspect-[4/3] w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="font-label text-[10px] uppercase tracking-[0.24em] text-accent-300">
                  Recommended Setting
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-100">
                  留出完整的两个半小时，关掉弹窗提醒，让节奏自己展开。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 站点简介 */}
      <section className="border-t border-background-800/70 bg-background-950 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-8">
          <div className="flex flex-col gap-8 rounded-lg border border-background-800/80 bg-background-900/40 p-6 md:p-10 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-heading text-xl text-foreground-50 md:text-2xl">
                一个由影迷整理的电影资料站
              </h2>
              <p className="mt-3 text-sm leading-[1.9] text-foreground-400">
                我们不追逐热闹的解读，只把可以查证的信息、可以复核的镜头与可以讨论的观点放在一起。站内所有内容均为虚构示例，用于展示资料型影视网站的结构与阅读体验。
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-700 px-5 text-sm text-foreground-200 transition-colors hover:border-accent-600/60 hover:text-accent-300"
              >
                关于我们
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/category"
                className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm text-foreground-50 transition-colors hover:bg-primary-600"
              >
                进入资料库
                <i className="ri-archive-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}